/**
 * Pure H OS · Sprint 3 · Marketing Overview.
 * Composes Card, SectionHeader, DeltaChip, SeverityBadge, ListRow.
 */
import { useNavigate } from "react-router-dom";
import { Sparkles, TrendingUp, Sunrise, ArrowRight } from "lucide-react";
import { Card, SectionHeader, DeltaChip } from "../../components/lib";
import { ListRow } from "../../components/lib.overlay";
import { MARKETING_OVERVIEW, CAMPAIGNS, TASKS } from "../../mocks/marketingData";

const TAG_STYLES: Record<string, { fg: string; bg: string; label: string }> = {
  risk:        { fg: "var(--ph-danger)",  bg: "var(--ph-danger-tint)",  label: "Risk" },
  opportunity: { fg: "var(--ph-success)", bg: "var(--ph-success-tint)", label: "Opportunity" },
  insight:     { fg: "var(--ph-warning)", bg: "var(--ph-warning-tint)", label: "Insight" },
};

export default function Overview() {
  const navigate = useNavigate();
  const inFlight = CAMPAIGNS.filter((c) => c.status === "in-flight" || c.status === "live");
  const urgentTasks = TASKS.filter((t) => t.severity === "high" && t.status !== "done").slice(0, 5);

  return (
    <div className="space-y-6" data-testid="mkt-overview">
      <Card testId="mkt-kpis">
        <SectionHeader eyebrow="Snapshot" title="Marketing Performance" action={<TrendingUp size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {MARKETING_OVERVIEW.kpis.map((k) => (
            <div key={k.key} data-testid={`mkt-kpi-${k.key}`}>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-ink-muted)" }}>{k.label}</div>
              <div className="mt-1 text-xl font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)", letterSpacing: "-0.01em" }}>{k.value}</div>
              <div className="mt-1 flex items-center gap-2">
                <DeltaChip delta={k.delta} trend={k.trend as any} />
                {k.hint && <span className="text-[10px]" style={{ color: "var(--ph-ink-soft)" }}>{k.hint}</span>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card testId="mkt-inflight">
            <SectionHeader eyebrow="Now" title="Campaigns in flight" action={<Sunrise size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
            <div className="space-y-3">
              {inFlight.map((c) => (
                <div key={c.id} data-testid={`mkt-inflight-${c.id}`} className="flex items-center gap-3 p-3 rounded"
                  style={{ border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
                  <div style={{ width: 6, height: 40, borderRadius: 3, background: c.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{c.name}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--ph-ink-muted)" }}>
                      {c.sku} · {c.channels.join(" · ")} · owner {c.owner}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>{c.roas.toFixed(1)}×</div>
                    <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>ROAS</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card testId="mkt-ai-signals">
            <SectionHeader eyebrow="AI" title="Marketing signals" action={<Sparkles size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
            <div className="space-y-4">
              {MARKETING_OVERVIEW.signals.map((s) => {
                const t = TAG_STYLES[s.tag];
                return (
                  <div key={s.id} data-testid={`mkt-signal-${s.id}`} className="pb-4 border-b last:border-b-0 last:pb-0" style={{ borderColor: "var(--ph-border-subtle)" }}>
                    <div className="flex items-start gap-3">
                      <span className="px-2 py-0.5 text-[10px] font-semibold uppercase flex-shrink-0"
                        style={{ background: t.bg, color: t.fg, letterSpacing: "0.14em", borderRadius: "var(--ph-radius-sm)" }}>
                        {t.label}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{s.title}</div>
                        <div className="text-xs mt-1" style={{ color: "var(--ph-ink-muted)" }}>{s.body}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card testId="mkt-urgent-tasks">
            <SectionHeader
              eyebrow="Today"
              title="Urgent tasks"
              action={
                <button
                  data-testid="mkt-open-tasks"
                  className="text-xs font-medium"
                  onClick={() => navigate("/pureh/marketing/tasks")}
                  style={{ color: "var(--ph-brand-primary)" }}
                >
                  Open queue →
                </button>
              }
            />
            <div className="space-y-1">
              {urgentTasks.length === 0 ? (
                <div className="py-4 text-sm text-center" style={{ color: "var(--ph-ink-muted)" }}>All clear.</div>
              ) : urgentTasks.map((t) => (
                <ListRow
                  key={t.id}
                  testId={`mkt-urgent-${t.id}`}
                  label={t.title}
                  sublabel={<>{t.owner} · <span style={{ color: "var(--ph-danger)" }}>{t.dueRelative}</span></>}
                  trailing={<ArrowRight size={12} style={{ color: "var(--ph-ink-soft)" }} />}
                  onClick={() => navigate("/pureh/marketing/tasks")}
                />
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
